import { useCallback, useEffect, useState } from 'react';
import type {
  ConsumerPartialPaymentAllocationModel,
  ConsumerPartialPaymentResponseModel,
} from './failed-payments.types';

type PartialPaymentPreviewApiClient = {
  post: <T>(url: string, body: unknown) => Promise<T>;
};

const PREVIEW_DEBOUNCE_MS = 300;
const NO_ALLOCATIONS: ConsumerPartialPaymentAllocationModel[] = [];

type PartialPaymentAllocationPreviewState = {
  allocations: ConsumerPartialPaymentAllocationModel[];
  loading: boolean;
  error: boolean;
  retry: () => void;
};

/**
 * Fetches the backend-decided partial-payment allocation for the entered amount. The allocation
 * order (Fees > Rent > Deposit, oldest due date first) lives entirely on the backend; the client
 * only renders the result and feeds the scheduled remainder into the late-fee preview.
 */
export function usePartialPaymentAllocationPreview({
  apiClient,
  amount,
  enabled,
}: {
  apiClient: PartialPaymentPreviewApiClient;
  amount: number;
  enabled: boolean;
}): PartialPaymentAllocationPreviewState {
  const isValidAmount = enabled && Number.isFinite(amount) && amount > 0;
  const [retryKey, setRetryKey] = useState(0);
  const requestKey = `${amount}:${retryKey}`;
  const [allocations, setAllocations] =
    useState<ConsumerPartialPaymentAllocationModel[]>(NO_ALLOCATIONS);
  const [previewedRequestKey, setPreviewedRequestKey] = useState<string | null>(
    null,
  );
  const [errorRequestKey, setErrorRequestKey] = useState<string | null>(null);
  const retry = useCallback(() => {
    setRetryKey((current) => current + 1);
  }, []);

  useEffect(() => {
    if (!isValidAmount) {
      return;
    }

    let cancelled = false;

    const timeout = setTimeout(() => {
      async function fetchPreview() {
        try {
          const response =
            await apiClient.post<ConsumerPartialPaymentResponseModel>(
              '/v2/users/failed-payments/payments/partial/preview',
              { amount },
            );
          if (!cancelled) {
            setAllocations(response.allocations ?? NO_ALLOCATIONS);
            setPreviewedRequestKey(requestKey);
            setErrorRequestKey(null);
          }
        } catch {
          if (!cancelled) {
            setAllocations(NO_ALLOCATIONS);
            setPreviewedRequestKey(null);
            setErrorRequestKey(requestKey);
          }
        }
      }

      fetchPreview();
    }, PREVIEW_DEBOUNCE_MS);

    return () => {
      cancelled = true;
      clearTimeout(timeout);
    };
  }, [apiClient, amount, isValidAmount, requestKey]);

  const hasCurrentPreview = isValidAmount && previewedRequestKey === requestKey;
  const hasCurrentError = isValidAmount && errorRequestKey === requestKey;

  return {
    allocations: hasCurrentPreview ? allocations : NO_ALLOCATIONS,
    loading: isValidAmount && !hasCurrentPreview && !hasCurrentError,
    error: hasCurrentError,
    retry,
  };
}
